<template>
	<div class="widget widget-myday" :class="{ 'fullscreen-widget': isFullscreen }" ref="root">
		<v-card class="mx-auto" flat>
			<template v-if="!isLoading">
				<v-toolbar color="transparent" @click="toggleFullscreen">
					<div class="v-card-top-actions" v-if="isFullscreen">
						<span>
							<md-icon>chevron_left</md-icon>
							{{ t('Back') }}
						</span>
					</div>
					<v-toolbar-title>
						<md-icon>today</md-icon>
						{{ title }}
					</v-toolbar-title>
					<div class="v-card-top-actions">
						<md-icon v-if="!isFullscreen">arrow_circle_right</md-icon>
					</div>
				</v-toolbar>
				<v-card-text>
					<div :class="timelineClasses" ref="timelineContainerRef">
						<div class="myday-header" ref="timelineHeaderRef" @click="onTimelineHeaderClick" v-if="isTouchMode && isFullscreen">
							<div class="drag-bar"></div>
							<h4>{{ selectedDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}</h4>
						</div>

						<div class="myday-appointments" v-if="!isTouchMode || !isFullscreen">
							<div>
								<div class="add-appointment" @click="goToPlanning">
									<span class="text-overflow">{{ t('AddAppointment') }}</span>
									<md-icon>calendar_add_on</md-icon>
								</div>
							</div>
							<div>
								<div class="visits-objective" @click="goToPlanning">
									<div class="background-fill" :style="{ height: ratio + '%' }"></div>
									<span class="text-overflow">{{ t('Visits') }}</span>
									<span class="count">{{ nbVisit }}/{{ nbVisitTotal }}</span>
								</div>
							</div>
							<div v-if="hasAssortOrder">
								<div class="sales" @click="goToOrdersTracking">
									<span class="text-overflow">{{ t('Sales') }}</span>
									<span class="count">{{ salesAmount }}</span>
								</div>
							</div>
						</div>

						<div class="appointment-route-infos" v-if="!isFullscreen">
							<div>
								<md-icon>store</md-icon>
								<span>{{ visitDuration }}</span>
							</div>
							<div>
								<md-icon>directions_car</md-icon>
								<span>{{ visitRouteDuration }}</span>
							</div>
							<div>
								<div class="total-duration">
									<md-icon>store</md-icon>
									<md-icon>add</md-icon>
									<md-icon>directions_car</md-icon>
								</div>
								<span :class="routeGapStatus?.class">
									<md-icon v-if="routeGapStatus?.warning">warning</md-icon>
									{{ visitRouteDurationTotal }}
								</span>
							</div>
							<div @click="drawRoutes" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<md-icon>conversion_path</md-icon>
								<span>{{ t('SeeAllRoutes') }}</span>
							</div>
						</div>

						<div class="appointment-route-infos" v-if="isFullscreen && isTouchMode">
							<v-slide-group show-arrows>
								<v-slide-group-item>
									<v-chip @click="goToNextAppointment" v-if="todayAppointments.length > 0 && !isAllValidated()" class="main-chip ma-1">
										<md-icon>directions</md-icon>
										<span>{{ t('GoToNextAppointment') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip @click="drawRoutes" v-if="todayAppointments.length > 0 && !isAllValidated()" class="ma-1">
										<md-icon>conversion_path</md-icon>
										<span>{{ t('SeeAllRoutes') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip @click="openInApp()" v-if="todayAppointments.length > 0 && !isAllValidated()" class="ma-1">
										<md-icon>navigation</md-icon>
										<span>{{ t('OpenInApp') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<md-icon>store</md-icon>
										<span>{{ visitDuration }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<md-icon>directions_car</md-icon>
										<span>{{ visitRouteDuration }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<div class="total-duration">
											<md-icon>store</md-icon>
											<md-icon>add</md-icon>
											<md-icon>directions_car</md-icon>
										</div>
										<span :class="routeGapStatus?.class">
											<md-icon v-if="routeGapStatus?.warning">warning</md-icon>
											{{ visitRouteDurationTotal }}
										</span>
									</v-chip>
								</v-slide-group-item>
							</v-slide-group>
						</div>

						<v-timeline v-if="todayAppointments.length > 0" density="compact" class="deliveryLocations-timeline" truncate-line="both" :style="{ '--progress-percentage': progressPercentage + '%', '--completed-percentage': completedAppointmentsPercentage + '%', '--route-start': getRouteSegmentStart() + '%', '--route-end': getRouteSegmentEnd() + '%' }">
							<div class="timeline-progress-bar" ref="timelineProgressBar"></div>
							<template v-for="(todayAppointment, index) in todayAppointments" :key="index">
								<v-timeline-item v-if="nextAppointmentAfterCompleted && index === nextAppointmentAfterCompleted.index && getRouteInfoForIndex(index)" dot-color="grey-lighten-1" size="x-small" class="route-info-item">
									<template #icon>
										<md-icon style="font-size: 24px; width: 40px; height: 40px; color: #1c79bb; display: flex; align-items: center; justify-content: center; background-color: #f0f0f0; border-radius: 50%; border: 2px solid #1c79bb">directions</md-icon>
									</template>
									<div class="route-info">
										<div class="route-details">
											<span class="route-distance">
												<md-icon>straighten</md-icon>
												{{ getRouteInfoForIndex(index).distance }}
											</span>
											<span class="route-time">
												<md-icon>schedule</md-icon>
												{{ getRouteInfoForIndex(index).duration }}
											</span>
										</div>
									</div>
								</v-timeline-item>

								<!-- Appointment item -->
								<v-timeline-item :dot-color="todayAppointment.color" size="large" :class="todayAppointment.isHighlighted ? 'highlighted' : ''">
									<template #icon>
										<md-icon class="cursor-pointer" @click="seeAppointment(todayAppointment)">{{ todayAppointment.icon }}</md-icon>
									</template>
									<div class="deliveryLocation-item" :data-appointment-uid="todayAppointment.uid">
										<div class="deliveryLocation-item-line">
											<div class="delivery-location-sps-container" v-if="isFullscreen && todayAppointment.sps && !isMobile">
												<span class="delivery-location-sps">{{ todayAppointment.sps }}</span>
											</div>
											<div class="delivery-location-text">
												<span class="delivery-location-name text-secondary" @click="seeAppointment(todayAppointment)">
													{{ getDeliveryLocationChannel(todayAppointment) }}
													{{ !isPrivate(todayAppointment) ? todayAppointment.deliveryLocation?.label : t('PRIVATE') }}
												</span>
												<span class="delivery-location-info text-on-surface">
													<span v-if="todayAppointment.startDate">{{ new Date(todayAppointment.startDate).toLocaleDateString() }} {{ new Date(todayAppointment.startDate).toLocaleTimeString().substring(0, 5) }} - {{ new Date(todayAppointment.endDate).toLocaleTimeString().substring(0, 5) }} |</span>
													{{ todayAppointment.subTypeLabel }}
												</span>
											</div>
										</div>

										<div class="icons" v-if="!isFullscreen || (isFullscreen && isMobile)">
											<md-icon :class="todayAppointment.iconStatusClass">{{ todayAppointment.iconStatus }}</md-icon>
											<v-menu :model-value="openMenuId === todayAppointment.uid + index" @update:model-value="(val) => (openMenuId = val ? todayAppointment.uid + index : null)" v-bind="menuProps" teleport="false">
												<template v-slot:activator="{ props }">
													<span v-bind="props" :ref="'menuActivator-' + todayAppointment.uid + index">
														<md-icon>more_vert</md-icon>
													</span>
												</template>
												<v-list>
													<v-list-item v-if="iframeDataBridge.isOnline()" @click="handleMenuClick(todayAppointment, 'map')">
														<v-list-item-title>{{ t('SeeOnMap') }}</v-list-item-title>
													</v-list-item>
													<v-list-item v-if="!isUserHome(todayAppointment) && !isValidated(todayAppointment)" @click="handleMenuClick(todayAppointment, 'cancel')">
														<v-list-item-title>{{ t('Cancel') }}</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
										<div class="icons" v-else>
											<md-icon :class="todayAppointment.iconStatusClass">{{ todayAppointment.iconStatus }}</md-icon>
											<md-icon v-if="!isUserHome(todayAppointment) && !isValidated(todayAppointment)" @click="cancelAppointment(todayAppointment)" class="text-danger">cancel</md-icon>
											<md-icon v-if="iframeDataBridge.isOnline()" @click="showOnMap(todayAppointment)">location_on</md-icon>
										</div>
									</div>
								</v-timeline-item>
							</template>
						</v-timeline>

						<div v-if="todayAppointments.length === 0" class="no-appointments-message">
							<md-icon class="no-appointments-icon">event_busy</md-icon>
							<p>{{ t('NoAppointments') }}</p>
						</div>
					</div>

					<div :class="'right-container' + (timelineClasses['touch-mode'] ? ' touch-mode' : '')">
						<div class="appointment-route-infos" v-if="isFullscreen && !isTouchMode">
							<div>
								<md-icon>store</md-icon>
								<span>{{ visitDuration }}</span>
							</div>
							<div>
								<md-icon>directions_car</md-icon>
								<span>{{ visitRouteDuration }}</span>
							</div>
							<div>
								<div class="total-duration">
									<md-icon>store</md-icon>
									<md-icon>add</md-icon>
									<md-icon>directions_car</md-icon>
								</div>
								<span :class="routeGapStatus?.class">
									<md-icon v-if="routeGapStatus?.warning">warning</md-icon>
									{{ visitRouteDurationTotal }}
								</span>
							</div>
							<div @click="goToNextAppointment" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<md-icon>directions</md-icon>
								<span>{{ t('GoToNextAppointment') }}</span>
							</div>
							<div @click="drawRoutes" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<md-icon>conversion_path</md-icon>
								<span>{{ t('SeeAllRoutes') }}</span>
							</div>
							<div v-if="hasRoute && todayAppointments.length > 0 && !isAllValidated()" @click="openInApp()" class="cursor-pointer">
								<md-icon>navigation</md-icon>
								<span>{{ t('OpenInApp') }}</span>
							</div>
						</div>

						<MapLibre
							ref="mapRef"
							:locations="mapLocations"
							:all-locations="allLocations"
							:initial-zoom="10"
							:show-popup-on-click="true"
							:detail-marker-renderer="renderMarkerDetail"
							:tool-box="toolBox"
							:route-geo-json="routeGeoJson"
							:selected-date="selectedDate"
							location-id-key="id"
							location-title-key="label"
							:filter-component="DeliveryLocationFiltersDialog"
							@marker-click="onMarkerClick"
							@toolbox-action="onToolboxAction"
							@filters-changed="onFiltersChanged"
							@action-focus="handleActionFocus"
							@edit-delivery-location="(id) => handleEditDeliveryLocation(id, () => isFullscreen.value && toggleFullscreen())"
							@add-new-visit="handleAddNewVisit"
							@phone-number-copied="handlePhoneNumberCopied"
							:hide-detail-on-mini="true"
							@marker-detail-hidden="markerDetailHidden"
						/>
					</div>
				</v-card-text>
			</template>
			<template v-else>
				<v-skeleton-loader type="card, list-item, list-item-two-line, list-item-two-line" />
			</template>
		</v-card>

		<v-snackbar v-model="searchingPosition" :timeout="-1" :loading="true" rounded="xl" color="info">
			{{ t('SearchingUserPosition') }}
		</v-snackbar>
	</div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, onBeforeUnmount, computed, watch, inject, nextTick } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import MapLibre from '../map/MapLibre.vue';
import { useTranslator } from '@/utils/useTranslator';
import { eventBus } from '@/utils/eventBus';
import { useVisibleRefresh } from '@/utils/useVisibleRefresh';
import { handleEditDeliveryLocation, handleAddNewVisit, handlePhoneNumberCopied, getDeliveryLocationTypeIcon, getDeliveryLocationTypeColor, getDeliveryLocationKPIs, getVisitFrequency, getLastVisitInfo, resetDetailMarkerData } from '@/utils/appointmentUtils';
import { routeService } from '@/services/routeService';
import { appointmentService } from '@/services/appointmentService';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import { deviceUtils } from '@/utils/deviceUtils';
import { overlayHandler } from '@/utils/overlayHandler';
import { dragBottomSheetHandler } from '@/utils/dragBottomSheetHandler';
import { buildGoogleMapsDirUrl, buildGeoMultiUrl, openNavigationInApp } from '@/utils/navigationUtils';

const root = ref(null);
const t = useTranslator();
const sfaHelper = iframeDataBridge.getSfaHelper();
const salesHelper = iframeDataBridge.getSalesHelper();
const dateHelper = iframeDataBridge.getDateHelper();
const enumAppointment = iframeDataBridge.enumAppointment;
const enumProduct = iframeDataBridge.enumProduct;
const user = iframeDataBridge.getCurrentUser();
const routeGeoJson = ref(null);
const toast = iframeDataBridge.getIframeWindow().toast;
const iframeDocument = iframeDataBridge.getIframeWindow().document;

// #endregion

// #region STATE

const props = defineProps({
	title: String,
	selectedDate: Date,
});

const isLoading = ref(true);
const isFullscreen = ref(false);
const isTouchMode = ref(deviceUtils.isTouchMode());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode() && isFullscreen.value;
	isMobile.value = deviceUtils.isMobile();
	resizeProgressBar();
};
const isMobile = ref(deviceUtils.isMobile());
const overlayTarget = inject('overlayTarget', null);
const menuProps = computed(() => ({
	attach: overlayTarget || undefined,
	scrim: true,
	scrollStrategy: 'close',
}));
const openMenuId = ref(null);
watch(openMenuId, () => {
	overlayHandler(openMenuId);
});
const selectedAppointmentForNavigation = ref(null);
const hasAssortOrder = ref(true);

const mapControl = inject('mapControl');
const mapRef = ref(null);
const showMap = ref(false);
const mapLocations = ref([]);
const allLocations = ref([]);
const deliveryLocationsGeo = ref([]);
const toolBox = ref(['allDeliveryLocations', 'offline']);
const userCurrentPosition = ref(null);
const searchingPosition = ref(false);
const hasRoute = ref({
	active: false,
	waypoints: [],
});
let mapScriptLoadingPromise = null;

const todayAppointments = ref([]);
const nbVisit = ref(0);
const nbVisitTotal = ref(0);

const salesAmount = ref(0);
const visitDuration = ref(0);
const visitRouteDuration = ref(null);
const visitRouteDurationTotal = ref(null);
const routeGapStatus = ref(null);

const timelineProgressBar = ref(null);
const progressPercentage = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return 0;
	return calculateDayProgress();
});

const completedAppointmentsPercentage = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return 0;

	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	if (nonUserAppointments.length === 0) return 0;

	let lastCompletedIndex = -1;
	for (let i = 0; i < nonUserAppointments.length; i++) {
		if (nonUserAppointments[i].status === enumAppointment.status.VALIDATED) {
			lastCompletedIndex = i;
		}
	}

	const totalTimelineItems = todayAppointments.value.length;

	if (lastCompletedIndex === -1) return (1 / totalTimelineItems) * 100;
	let lastCompletedInTimeline = -1;
	for (let i = 0; i < todayAppointments.value.length; i++) {
		const appointment = todayAppointments.value[i];
		if (appointment.deliveryLocationId !== user.deliveryLocationHomeId && appointment.status === enumAppointment.status.VALIDATED && nonUserAppointments.indexOf(appointment) === lastCompletedIndex) {
			lastCompletedInTimeline = i;
			break;
		}
	}

	if (lastCompletedInTimeline === -1) return 1;

	return ((lastCompletedInTimeline + 0.5) / totalTimelineItems) * 100;
});

const nextAppointmentAfterCompleted = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return null;

	let lastCompletedIndex = -1;
	for (let i = todayAppointments.value.length - 1; i >= 0; i--) {
		const apt = todayAppointments.value[i];
		if (apt.status === enumAppointment.status.VALIDATED && apt.deliveryLocationId !== user.deliveryLocationHomeId) {
			lastCompletedIndex = i;
			break;
		}
	}

	let nextIndex = lastCompletedIndex + 1;

	if (lastCompletedIndex === -1) {
		for (let i = 0; i < todayAppointments.value.length; i++) {
			const apt = todayAppointments.value[i];
			if (apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED) {
				nextIndex = i;
				break;
			}
		}
	}

	if (nextIndex >= todayAppointments.value.length) {
		return null;
	}

	for (let i = nextIndex; i < todayAppointments.value.length; i++) {
		const apt = todayAppointments.value[i];
		if (apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED) {
			return { appointment: apt, index: i };
		}
	}

	return null;
});

const nextAppointmentRoute = ref({
	distance: null,
	duration: null,
	nextAppointment: null,
});

const ratio = computed(() => {
	if (!nbVisitTotal.value || nbVisitTotal.value === 0) return 0;
	return Math.min((nbVisit.value / nbVisitTotal.value) * 100, 100).toFixed(2);
});

const dragSenderMode = ref('mid');
const timelineContainerRef = ref(null);
const timelineHeaderRef = ref(null);
const dragSenderIsDragging = ref(false);
const timelineContainerDrag = ref(null);

const timelineClasses = computed(() => {
	const cls = {
		'left-container': true,
		'touch-mode': isTouchMode.value,
	};
	if (isTouchMode.value) cls[dragSenderMode.value] = true;
	return cls;
});

// #endregion

// #region NAVIGATION APPS

function getNavigationLinks() {
	let points = [];

	if (hasRoute.value.active && Array.isArray(hasRoute.value.waypoints) && hasRoute.value.waypoints.length > 0) {
		points = hasRoute.value.waypoints.map((wp) => `${wp[1]},${wp[0]}`);
	} else {
		const appointment = selectedAppointmentForNavigation.value;
		const geo = appointment?.deliveryLocation?._geo;
		if (!geo) return null;

		if (userCurrentPosition.value) {
			points.push(`${userCurrentPosition.value.latitude},${userCurrentPosition.value.longitude}`);
		}

		points.push(`${geo.latitude},${geo.longitude}`);
	}

	if (points.length < 1) return null;

	const last = points[points.length - 1];

	return {
		geo: buildGeoMultiUrl(points),
		googleMaps: buildGoogleMapsDirUrl(points),
		waze: `https://waze.com/ul?ll=${encodeURIComponent(last)}&navigate=yes`,
		appleMaps: `http://maps.apple.com/?daddr=${encodeURIComponent(last)}&dirflg=d`,
	};
}

function openInApp() {
	let waypoints = [];
	if (hasRoute.value.active && Array.isArray(hasRoute.value.waypoints) && hasRoute.value.waypoints.length > 0) {
		waypoints = hasRoute.value.waypoints;
	} else {
		const appointment = selectedAppointmentForNavigation.value;
		const geo = appointment?.deliveryLocation?._geo;
		if (!geo) {
			console.warn('No navigation links available');
			return;
		}
		if (userCurrentPosition.value) {
			waypoints.push([userCurrentPosition.value.longitude, userCurrentPosition.value.latitude]);
		}
		waypoints.push([geo.longitude, geo.latitude]);
	}
	openNavigationInApp(waypoints);
}

// #endregion

// #region LIFECYCLE

const init = async () => {
	if (iframeDataBridge.isOnline()) {
		initializeGoogleMaps();
	}
	mapRef.value?.clearHtmlMarkers();
	resetRoutes();
	resizeProgressBar();

	await getTodayAppointments();
	await getAllDeliveryLocations();
	await getOrders();
	isLoading.value = false;
};

useVisibleRefresh('appointments-updated', init);

onMounted(() => {
	mapControl.drawMyDayRoutes = drawRoutes;
	iframeDataBridge.getStorage().get('DELIVERYLOCATIONGEO', (data) => {
		deliveryLocationsGeo.value = data || [];
	});
	window.addEventListener('resize', onResize);
	init();
});

onBeforeUnmount(() => {
	if (timelineContainerDrag.value) timelineContainerDrag.value();
	window.removeEventListener('resize', onResize);
});

watch(() => props.selectedDate, init, { immediate: true });

watch(
	() => isTouchMode.value,
	async (v) => {
		if (!isFullscreen.value) return;
		await nextTick();

		if (timelineContainerDrag.value) timelineContainerDrag.value();
		timelineContainerDrag.value = null;
		if (!v) return;

		const panelEl = timelineContainerRef.value;
		const handleEl = timelineHeaderRef.value;

		timelineContainerDrag.value = dragBottomSheetHandler(panelEl, handleEl, {
			isTouchMode,
			dragSenderMode,
			dragSenderIsDragging,
			iframeDocument,
			getSnaps: () => {
				const vh = window.innerHeight || iframeDocument.documentElement.clientHeight || 0;
				const topFull = 0;
				const topMid = Math.round(vh * 0.5);
				const topMini = Math.max(0, vh - 50 - 110);
				return { vh, topFull, topMid, topMini };
			},
		});
	},
	{ flush: 'post' }
);

watch(
	() => isFullscreen.value,
	async (v) => {
		isTouchMode.value = deviceUtils.isTouchMode() && v;
		if (!v) return;
		await nextTick();

		if (timelineContainerDrag.value) timelineContainerDrag.value();
		timelineContainerDrag.value = null;

		const panelEl = timelineContainerRef.value;
		const handleEl = timelineHeaderRef.value;

		timelineContainerDrag.value = dragBottomSheetHandler(panelEl, handleEl, {
			isTouchMode,
			dragSenderMode,
			dragSenderIsDragging,
			iframeDocument,
			getSnaps: () => {
				const vh = window.innerHeight || iframeDocument.documentElement.clientHeight || 0;
				const topFull = 0;
				const topMid = Math.round(vh * 0.5);
				const topMini = Math.max(0, vh - 50 - 110);
				return { vh, topFull, topMid, topMini };
			},
		});
	}
);

watch(
	() => dragSenderMode.value,
	async () => {
		await nextTick();
		resizeProgressBar();

		if (dragSenderMode.value == 'mid') {
			scrollToNextAppointment();
		}
	}
);

// #endregion

// #region GOOGLE MAPS

function loadGoogleMaps() {
	if (!iframeDataBridge.isOnline()) return Promise.resolve();
	if (window.google?.maps) return Promise.resolve();
	if (mapScriptLoadingPromise) return mapScriptLoadingPromise;

	mapScriptLoadingPromise = new Promise((resolve) => {
		window.initMap = () => {
			resolve();
			window.initMap = undefined;
		};
		const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&callback=initMap&loading=async&libraries=marker`;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	});

	return mapScriptLoadingPromise;
}

async function initializeGoogleMaps() {
	if (!iframeDataBridge.isOnline()) return;
	await loadGoogleMaps();
}

// #endregion

// #region APPOINTMENTS/DELIVERYLOCATIONS - DATA FETCHING

function getTodayAppointments() {
	return new Promise(async (resolve, reject) => {
		try {
			const callback = getTodayAppointments;

			if (iframeDataBridge.isSyncEnded(['SFA_APPOINTMENTS', 'DELIVERYLOCATIONS'], callback)) {
				let startDate = new Date(props.selectedDate);
				startDate.setHours(0, 0, 0, 0);
				let endDate = new Date(startDate);
				endDate.setHours(23, 59, 59, 999);

				const success = async function (result) {
					try {
						const externalSubTypeId = enumAppointment.getSubTypeId('EXTERNAL');
						let appointments = result.filter((appointment) => {
							return appointment.status !== enumAppointment.status.CANCELLED && dateHelper.isDateGreater(appointment.startDate, startDate, true) && dateHelper.isDateLess(appointment.startDate, endDate, true) && appointment.subType !== externalSubTypeId;
						});

						todayAppointments.value = appointments.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
						getNbAppointments(todayAppointments.value);
						getVisitDurations(todayAppointments.value);
						getVisitRouteDuration(todayAppointments.value);
						getVisitRouteDurationTotal();
						await getAppointmentDeliveryLocations();

						getUserAppointment();

						resolve();
					} catch (err) {
						console.error('Error in getTodayAppointments success callback:', err);
						reject(err);
					}
				};

				const params = {
					startDate: startDate.toISOString(),
					endDate: endDate.toISOString(),
					userId: user.id,
				};
				await appointmentService.getAppointments(params, success);
			} else {
				resolve();
			}
		} catch (error) {
			console.error('Error in getTodayAppointments:', error);
			reject(error);
		}
	});
}

function getUserAppointment() {
	if (todayAppointments.value.length === 0) return;

	const ecosVM = iframeDataBridge.getEcosVM();
	let userHome = ecosVM?.deliveryLocations?.filter((deliveryLocation) => deliveryLocation.id === user.deliveryLocationHomeId);
	let isStartPositionNight = todayAppointments.value.length > 0 && todayAppointments.value[0].subType === sfaHelper.appointment.getDefaultSubtypeNight();
	let isEndPositionNight = todayAppointments.value.length > 0 && todayAppointments.value[todayAppointments.value.length - 1].subType === sfaHelper.appointment.getDefaultSubtypeNight();

	let userAppointment = {
		uid: 'home',
		label: t('UserHome'),
		startDate: null,
		endDate: null,
		deliveryLocationId: user.deliveryLocationHomeId,
		deliveryLocation: {
			id: userHome && userHome.length > 0 ? userHome[0].id : user.deliveryLocationHomeId,
			label: userHome && userHome.length > 0 ? userHome[0].label : '',
		},
		color: sfaHelper.getAppointmentColor({
			type: enumAppointment.type.CL,
			status: enumAppointment.status.VALIDATED,
		}),
		icon: 'home',
	};

	const hasStartUserHome = todayAppointments.value.map((ta) => ta.deliveryLocationId).indexOf(user.deliveryLocationHomeId) === 0;
	if (userHome && !isStartPositionNight && !hasStartUserHome) todayAppointments.value.unshift(userAppointment);

	const hasEndUserHome = todayAppointments.value.map((ta) => ta.deliveryLocationId).lastIndexOf(user.deliveryLocationHomeId) === todayAppointments.value.length - 1;
	if (userHome && !isEndPositionNight && !hasEndUserHome) todayAppointments.value.push(userAppointment);
}

function getAllDeliveryLocations() {
	return new Promise((resolve) => {
		const callback = getAllDeliveryLocations;
		if (iframeDataBridge.isSyncEnded(['DELIVERYLOCATIONS'], callback)) {
			let success = function (result) {
				allLocations.value = result;
				resolve();
			};

			if (iframeDataBridge.isOnline()) {
				const url = iframeDataBridge.getService().sales.deliveryLocations.getUrl();
				const method = iframeDataBridge.getService().sales.deliveryLocations.type || 'get';
				const data = { userId: user.id };
				const headers = { Token: user.tokenSales };

				axios({
					method: method.toLowerCase(),
					url,
					headers,
					...(method.toUpperCase() === 'GET' ? { params: data } : { data }),
				})
					.then((response) => success(response.data))
					.catch((error) => {
						console.log(error.response || error);
						resolve();
					});
			} else {
				iframeDataBridge.getStorage().get('DELIVERYLOCATIONS', success);
			}
		}
	});
}

// #endregion

// #region APPOINTMENTS - DATA PROCESSING

async function getAppointmentDeliveryLocations() {
	const deliveryLocations = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONS');

	for (const todayAppointment of todayAppointments.value) {
		const deliveryLocation = deliveryLocations.find((dl) => dl.id === todayAppointment.deliveryLocationId);
		if (!deliveryLocation) continue;

		todayAppointment.color = getDeliveryLocationTypeColor(deliveryLocation, todayAppointment);
		let defaultTypeIcon = getDeliveryLocationTypeIcon(deliveryLocation);
		todayAppointment.icon = !todayAppointment.icon ? defaultTypeIcon : todayAppointment.icon;
		todayAppointment.deliveryLocation = deliveryLocation;

		let iconStatus = null;
		let iconStatusClass = null;

		if (sfaHelper.appointment.isInPrepa(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.PREPA;
		}

		if (sfaHelper.appointment.isInProgress(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.INPROGRESS;
		}

		if (await hasSyncError(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.SYNCERROR;
			iconStatusClass = 'text-danger';
		} else if (await hasPendingSync(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.PENDINGSYNC;
			iconStatusClass = 'text-warning';

			if (todayAppointment.status === enumAppointment.status.VALIDATED || todayAppointment._mustValidate) {
				iconStatusClass = 'text-warning';
			}
		}

		todayAppointment.iconStatus = iconStatus;
		todayAppointment.iconStatusClass = iconStatusClass;

		todayAppointment.sps = await getSPS(todayAppointment);

		const subTypes = iframeDataBridge.getEcosVM().appointmentSubtypes;
		const appointmentSubType = subTypes.find((subType) => subType.id === todayAppointment.subType);
		todayAppointment.subTypeLabel = appointmentSubType ? t(appointmentSubType.label) : '';
	}

	updateMapLocations();
}

function calculateDayProgress() {
	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	const completedAppointments = nonUserAppointments.filter((ta) => ta.status === enumAppointment.status.VALIDATED);

	if (nonUserAppointments.length === 0) {
		return 0;
	}

	return (completedAppointments.length / nonUserAppointments.length) * 100;
}

async function hasSyncError(appointment) {
	const offlineAppointments = await iframeDataBridge.getStorageAsync('OFFLINE-SFA_Appointments');

	if (!offlineAppointments) {
		return false;
	}

	return iframeDataBridge.getEcosVM()._OfflineErrors.some((obj) => obj.appointment.uid === appointment.uid) || (appointment.status !== enumAppointment.status.VALIDATED && offlineAppointments.some((o) => (o._localId === appointment.uid || o.uid === appointment.uid) && o.status === enumAppointment.status.VALIDATED)) || appointment._mustValidate;
}

async function hasPendingSync(appointment) {
	const offlineAppointments = await iframeDataBridge.getStorageAsync('OFFLINE-SFA_Appointments');

	if (!offlineAppointments) {
		return false;
	}

	return offlineAppointments.some((o) => o._localId === appointment.uid || o.uid === appointment.uid);
}

function keepMainAppointments(appointments) {
	return appointments.filter((appointment) => {
		return appointment.type == enumAppointment.type.CL;
	});
}

function getNbAppointments(appointments) {
	appointments = keepMainAppointments(appointments);

	nbVisitTotal.value = appointments.length;
	nbVisit.value = appointments.filter((appointment) => {
		return appointment.status === enumAppointment.status.VALIDATED;
	}).length;
}

function getVisitDurations(appointments) {
	appointments = keepMainAppointments(appointments);

	const nbMinutes = appointments.reduce((total, appointment) => {
		const minutes = dateHelper.getDiffMinutes(appointment.endDate, appointment.startDate);
		return total + Math.abs(minutes);
	}, 0);

	const time = dateHelper.convertMinutesToHoursMinutes(nbMinutes);
	visitDuration.value = `${time.h}h${String(time.m).padStart(2, '0')}`;
}

function getVisitRouteDuration(appointments) {
	let routing = sfaHelper.getRouting();

	let startDate = new Date(props.selectedDate);
	startDate.setHours(0, 0, 0, 0);
	let endDate = new Date(startDate);
	endDate.setHours(23, 59, 59, 999);

	let routingVisits = appointments.filter((appointment) => {
		return (appointment.type === enumAppointment.type.CL || appointment.subType === enumAppointment.getSubTypeId('NIGHT')) && ((new Date(appointment.startDate) >= startDate && new Date(appointment.startDate) < endDate) || (new Date(appointment.endDate) >= startDate && new Date(appointment.endDate) < endDate)) && appointment.status !== enumAppointment.status.CANCELLED;
	});

	if (routing.enabled) {
		let routeDuration = 0;

		for (var i = 0; i < routingVisits.length; i++) {
			let v = routingVisits[i];
			if (i === 0 && routing.enableFirstRoute && v.prevApp && !dateHelper.isDateLess(v.startDate, startDate)) {
				routeDuration += v.prevApp.routeDuration;
			}

			if (i >= 0 && i < routingVisits.length - 1) {
				if (v.nextApp) {
					routeDuration += v.nextApp.routeDuration;
				}
			}

			if (i === routingVisits.length - 1 && routing.enableLastRoute) {
				if (v.nextApp && !dateHelper.isDateGreater(v.endDate, endDate)) {
					routeDuration += v.nextApp.routeDuration;
				}
			}
		}
		if (routeDuration > 0) {
			const time = dateHelper.convertMinutesToHoursMinutes(routeDuration);
			visitRouteDuration.value = `${time.h}h${String(time.m).padStart(2, '0')}`;
		} else {
			visitRouteDuration.value = '0h00';
		}
	}
}

function getVisitRouteDurationTotal() {
	if (!visitDuration.value || !visitRouteDuration.value) {
		visitRouteDurationTotal.value = '0h00';
		return;
	}

	const parse = (str) => str.split('h').map(Number);
	const [h1 = 0, m1 = 0] = parse(visitDuration.value);
	const [h2 = 0, m2 = 0] = parse(visitRouteDuration.value);

	const totalMinutes = (h1 + h2) * 60 + m1 + m2;
	const finalHours = Math.floor(totalMinutes / 60);
	const finalMinutes = totalMinutes % 60;

	visitRouteDurationTotal.value = `${finalHours}h${String(finalMinutes).padStart(2, '0')}`;

	getRouteGap(visitRouteDurationTotal.value);
}

function getRouteGap(visitRouteDurationTotal) {
	const settings = salesHelper.getModule(sfaHelper.getApplicationDomainIdSFA(), 'Legislation').settings;
	const maxMinutes = (Number(settings.find((s) => s.name === 'MaxConsecutiveWorkDuration')?.value) || 999) * 60;

	const [h = 0, m = 0] = visitRouteDurationTotal.split('h').map(Number);
	const totalMinutes = h * 60 + m;

	if (totalMinutes === 0) {
		routeGapStatus.value = { class: '', warning: false };
		return;
	}

	const routeGap = maxMinutes - totalMinutes;
	let css = null;
	let warning = false;

	if (-30 <= routeGap && routeGap <= 30) {
		css = 'text-success';
	} else if (routeGap > 30) {
		css = 'text-warning';
	} else if (routeGap < -30) {
		css = 'text-danger';
		warning = true;
	}

	routeGapStatus.value = { class: css, warning };
}

async function getSPS(appointment) {
	try {
		const spsValue = await sfaHelper.kpi.templates.getSPSValue(appointment.deliveryLocationId);
		return spsValue ?? null;
	} catch (e) {
		return null;
	}
}

function getDeliveryLocationChannel(todayAppointment) {
	if (todayAppointment.deliveryLocation?.channelId) {
		const channels = iframeDataBridge.getEcosVM().channels;
		const channel = channels.find((c) => c.id === todayAppointment.deliveryLocation.channelId);
		return channel ? channel.name + ' - ' || '' : '';
	}

	return '';
}

function isUserHome(todayAppointment) {
	return todayAppointment.deliveryLocationId === user.deliveryLocationHomeId;
}

function isPrivate(todayAppointment) {
	return todayAppointment.subType === enumAppointment.getSubTypeId('PRIVATE');
}

function isValidated(todayAppointment) {
	return todayAppointment.status === enumAppointment.status.VALIDATED;
}

function isAllValidated() {
	return todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId).every((ta) => ta.status === enumAppointment.status.VALIDATED);
}

// #endregion

// #region ACTIONS

function cancelAppointment(todayAppointment) {
	sfaHelper.cancelAppointment(todayAppointment, function () {
		amplify.publish('evtAgendaChange', { forceRefresh: true });
		eventBus.emit('appointments-updated');
	});
}

function showOnMap(todayAppointment) {
	if (!iframeDataBridge.isOnline()) {
		console.warn('Map is not available in offline mode');
		return;
	}
	if (!todayAppointment.deliveryLocationId) return;

	const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === todayAppointment.deliveryLocationId);

	if (!geo || !geo.latitude || !geo.longitude) {
		console.warn('No geo coordinates for this appointment');
		return;
	}

	updateMapLocations(true);
	showMap.value = true;

	if (isTouchMode.value && timelineContainerDrag.value && dragSenderMode.value !== 'mini') {
		timelineContainerDrag.value.setMode('mini');
	}

	if (!isFullscreen.value) {
		isFullscreen.value = true;
		eventBus.emit('widget-fullscreened', {
			widget: 'widget-myday',
			fullScreened: true,
			callback: async () => {
				resizeMap();
				await nextTick();
				if (mapRef.value && mapRef.value.setCenterZoom) {
					mapRef.value.setCenterZoom([geo.longitude, geo.latitude], 15);
				}
			},
		});
	} else {
		nextTick(() => {
			if (mapRef.value && mapRef.value.setCenterZoom) {
				mapRef.value.setCenterZoom([geo.longitude, geo.latitude], 15);
			}
		});
	}
}

async function goToNextAppointment() {
	hasRoute.value.active = false;
	hasRoute.value.waypoints = [];

	if (!iframeDataBridge.isOnline()) {
		console.warn('Cannot calculate route: offline mode');
		return;
	}

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}

	let startPosition = mapRef.value ? mapRef.value.getUserPosition() : null;

	if (!startPosition) {
		const userHomeGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === user.deliveryLocationHomeId);
		if (userHomeGeo && userHomeGeo.latitude && userHomeGeo.longitude) {
			startPosition = {
				lat: userHomeGeo.latitude,
				lng: userHomeGeo.longitude,
			};
			userCurrentPosition.value = startPosition;
		}
	}

	getNextAppointmentRouteInfo();

	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	let nextAppointmentIndex = -1;
	for (let i = 0; i < nonUserAppointments.length; i++) {
		if (nonUserAppointments[i].status !== enumAppointment.status.VALIDATED) {
			nextAppointmentIndex = i;
			break;
		}
	}

	if (nextAppointmentIndex >= 0 && mapRef.value) {
		const nextAppointment = nonUserAppointments[nextAppointmentIndex];
		const nextGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === nextAppointment.deliveryLocationId);

		if (nextGeo) {
			let fallbackCoords = null;

			for (let i = nextAppointmentIndex - 1; i >= 0; i--) {
				if (nonUserAppointments[i].status === enumAppointment.status.VALIDATED) {
					const lastCompleted = nonUserAppointments[i];
					const lastGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === lastCompleted.deliveryLocationId);
					if (lastGeo) {
						fallbackCoords = {
							lat: lastGeo.latitude,
							lng: lastGeo.longitude,
						};
						break;
					}
				}
			}

			if (!fallbackCoords && startPosition) {
				fallbackCoords = startPosition;
			}

			searchingPosition.value = true;
			try {
				await mapRef.value.navigateToLocation(nextGeo.latitude, nextGeo.longitude, nextAppointment.deliveryLocation?.label || 'Prochain RDV', fallbackCoords);
			} finally {
				searchingPosition.value = false;
			}

			hasRoute.value.active = true;
			hasRoute.value.waypoints = buildWaypoints(startPosition, {
				lat: nextGeo.latitude,
				lng: nextGeo.longitude,
			});
		} else {
			console.warn('No geo coordinates for the next appointment');
		}
	}

	dragSenderMode.value = 'mini';
}

async function zoomToNextAppointment() {
	const nextAppointment = nextAppointmentAfterCompleted.value;

	if (nextAppointment && mapRef.value) {
		const nextGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === nextAppointment.appointment.deliveryLocationId);

		if (nextGeo) {
			mapRef.value.setCenterZoom([nextGeo.longitude, nextGeo.latitude], 15);
		}
	}
}

async function scrollToNextAppointment() {
	const nextAppointment = nextAppointmentAfterCompleted.value;

	if (nextAppointment) {
		const timelineEl = timelineContainerRef.value.querySelector('.v-timeline');
		const appointmentEl = timelineEl.querySelector(`[data-appointment-uid="${nextAppointment.appointment.uid}"]`);

		if (appointmentEl) {
			setTimeout(() => {
				const timelineRect = timelineEl.getBoundingClientRect();
				const appointmentRect = appointmentEl.getBoundingClientRect();

				if (appointmentRect.top < timelineRect.top || appointmentRect.bottom > timelineRect.bottom) {
					const offset = appointmentRect.top - timelineRect.top - timelineRect.height / 2 + appointmentRect.height / 2;
					timelineEl.scrollBy({ top: offset, behavior: 'smooth' });
				}
			}, 300);
		}
	}
}

async function drawRoutes() {
	hasRoute.value.active = false;
	hasRoute.value.waypoints = [];

	if (!iframeDataBridge.isOnline()) {
		console.warn('Route calculation is not available in offline mode');
		toast('Route calculation is not available in offline mode', { type: 'warning' });
		return;
	}

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}

	const waypoints = await buildRoutesWaypoints();

	if (waypoints.length === 0) {
		console.warn('No appointments to display on the map');
		return;
	}

	showMap.value = true;
	if (iframeDataBridge.isOnline()) {
		routeGeoJson.value = await routeService.getRouteWithFallback(waypoints);
		hasRoute.value.active = true;
		hasRoute.value.waypoints = waypoints;
	} else {
		await drawDirectLines(appointmentsWithGeo);
	}

	getNextAppointmentRouteInfo();
	updateMapLocations();

	if (!isFullscreen.value) {
		isFullscreen.value = true;
		nextTick(() => {
			eventBus.emit('widget-fullscreened', {
				widget: 'widget-myday',
				fullScreened: true,
				callback: async () => {
					resizeMap();
					setTimeout(() => {
						if (mapRef.value) {
							mapRef.value.fitAll();
						}
					}, 1);
				},
			});
		});
	}

	await nextTick();
	mapRef.value.ensureSourcesAndLayers();
	dragSenderMode.value = 'mini';
}

function goToPlanning() {
	amplify.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vwSFAPlanning', data: { gotoDate: props.selectedDate.toISOString() } });
}

function goToOrdersTracking() {
	amplify.publish('evtChangeView', { appMenu: 'menuSALES', label: 'Order tracking Assort', view: 'vwOrdersTrackingAssort' });
}

function seeAppointment(todayAppointment) {
	if (!isUserHome(todayAppointment)) {
		const iframeWindow = iframeDataBridge.getIframeWindow();
		if (typeof iframeWindow?.externis?.sales?.vmAgenda == 'undefined') {
			iframeWindow.externis.sales.vmAgenda = {};
		}

		if (iframeWindow?.externis?.sales?.vmAgenda && iframeWindow.externis.sales.vmAgenda.datasource == null) {
			iframeWindow.externis.sales.vmAgenda.datasource = todayAppointments.value.map((a) => ({
				...a,
				startDate: a.startDate instanceof Date ? a.startDate : new Date(a.startDate),
				endDate: a.endDate instanceof Date ? a.endDate : new Date(a.endDate),
			}));
		}
		amplify.publish('evtAppointment', { appointment: todayAppointment });
	}
}

function onTimelineHeaderClick() {
	if (!isTouchMode.value) return;
	if (dragSenderIsDragging.value) return;
	if (typeof timelineContainerDrag.value?.setMode === 'function') {
		timelineContainerDrag.value.setMode(dragSenderMode.value == 'full' ? 'mini' : dragSenderMode.value == 'mini' ? 'mid' : 'full');
	}
}

function onMarkerClick(props) {
	if (isTouchMode.value && timelineContainerDrag.value && dragSenderMode.value !== 'mini') {
		timelineContainerDrag.value.setMode('mini');
	}
}

function markerDetailHidden() {
	highlightAppointment();
}

// #endregion

// #region ROUTE CALCULATION

function resetRoutes() {
	userCurrentPosition.value = null;
	routeGeoJson.value = null;

	nextAppointmentRoute.value = {
		distance: null,
		duration: null,
		nextAppointment: null,
		routesBetweenAppointments: [],
	};

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}
}

async function getNextAppointmentRouteInfo() {
	try {
		nextAppointmentRoute.value = {
			distance: null,
			duration: null,
			nextAppointment: null,
			routesBetweenAppointments: [],
		};

		const appointments = todayAppointments.value.filter((a) => a.deliveryLocationId !== user.deliveryLocationHomeId && a.status !== enumAppointment.status.CANCELLED);

		if (appointments.length === 0) return;

		const routes = [];
		userCurrentPosition.value = mapRef.value ? mapRef.value.getUserPosition() : null;

		for (let i = 0; i < appointments.length - 1; i++) {
			const fromAppt = appointments[i];
			const toAppt = appointments[i + 1];

			let origin = null;

			if (i === 0) {
				origin = userCurrentPosition.value || getGeoByDeliveryLocationId(user.deliveryLocationHomeId) || getGeoByDeliveryLocationId(fromAppt.deliveryLocationId);
			} else {
				origin = getGeoByDeliveryLocationId(fromAppt.deliveryLocationId);
			}

			const destination = getGeoByDeliveryLocationId(toAppt.deliveryLocationId);
			const waypoints = buildWaypoints(origin, destination);

			let routeInfo = null;

			if (waypoints && mapRef.value) {
				routeInfo = await mapRef.value.getRouteSummary(waypoints);
			}

			routes.push({
				fromIndex: i,
				toIndex: i + 1,
				fromAppointment: fromAppt,
				toAppointment: toAppt,
				distance: routeInfo?.distance || null,
				duration: routeInfo?.duration || null,
			});
		}

		nextAppointmentRoute.value.routesBetweenAppointments = routes;

		const lastCompletedIndex = appointments.map((a) => a.status === enumAppointment.status.VALIDATED).lastIndexOf(true);

		const nextIndex = lastCompletedIndex + 1;

		if (nextIndex >= appointments.length) return;

		const nextAppointment = appointments[nextIndex];
		nextAppointmentRoute.value.nextAppointment = nextAppointment;

		const fallbackPosition = lastCompletedIndex >= 0 ? getGeoByDeliveryLocationId(appointments[lastCompletedIndex].deliveryLocationId) : getGeoByDeliveryLocationId(user.deliveryLocationHomeId);

		const currentPosition = userCurrentPosition.value || fallbackPosition;

		if (!currentPosition) return;

		const nextGeo = getGeoByDeliveryLocationId(nextAppointment.deliveryLocationId);
		const waypoints = buildWaypoints(currentPosition, nextGeo);

		if (waypoints && mapRef.value) {
			const routeInfo = await mapRef.value.getRouteSummary(waypoints);
			if (routeInfo) {
				nextAppointmentRoute.value.distance = routeInfo.distance;
				nextAppointmentRoute.value.duration = routeInfo.duration;
			}
		}
	} catch (error) {
		console.error('Error calculating next appointment route info:', error);
	}
}

function getRouteInfoForIndex(index) {
	if (!nextAppointmentRoute.value.routesBetweenAppointments) return null;

	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	let nextAppointmentIndex = -1;
	for (let i = 0; i < nonUserAppointments.length; i++) {
		if (nonUserAppointments[i].status !== enumAppointment.status.VALIDATED) {
			nextAppointmentIndex = i;
			break;
		}
	}

	const currentAppointment = todayAppointments.value[index];
	const isNextUncompletedAppointment = nonUserAppointments[nextAppointmentIndex] && currentAppointment.deliveryLocationId === nonUserAppointments[nextAppointmentIndex].deliveryLocationId;

	if (isNextUncompletedAppointment && nextAppointmentRoute.value.distance && nextAppointmentRoute.value.duration) {
		return {
			distance: nextAppointmentRoute.value.distance,
			duration: nextAppointmentRoute.value.duration,
		};
	}

	return null;
}

function updateMapLocations(skipFitAll = false) {
	const needAdditionalLocations = mapRef.value?.getToolState('allDeliveryLocations')?.active;
	const additionnalLocations = needAdditionalLocations ? allLocations.value : null;
	const appointmentsToRoute = todayAppointments.value
		.filter((ta) => ta.deliveryLocation && ta.status != enumAppointment.status.CANCELLED)
		.map((ta) => {
			const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === ta.deliveryLocationId);

			return {
				...ta,
				geo: geo,
				hasValidGeo: !!(geo && geo.latitude && geo.longitude),
			};
		})
		.filter((ta) => ta.hasValidGeo);

	const hasRouteSequence = hasRoute.value?.active && Array.isArray(hasRoute.value.waypoints) && hasRoute.value.waypoints.length > 0;
	const sequenceMap = new Map();

	const pushSequence = (locationId, position) => {
		if (!locationId) return;
		if (!sequenceMap.has(locationId)) {
			sequenceMap.set(locationId, []);
		}
		sequenceMap.get(locationId).push(position);
	};

	const approxDistanceMeters = (lat1, lon1, lat2, lon2) => {
		const R = 6371000;
		const toRad = (d) => (d * Math.PI) / 180;
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	};

	const getSequenceLabel = (locationId) => {
		if (!sequenceMap.has(locationId)) return null;

		const positions = sequenceMap
			.get(locationId)
			.filter((p) => Number.isFinite(p))
			.sort((a, b) => a - b);

		if (positions.length === 0) return null;

		const first = positions[0];
		const last = positions[positions.length - 1];

		return first === last ? first.toString() : `${first}-${last}`;
	};

	if (hasRouteSequence) {
		const locIndex = appointmentsToRoute.map((ta) => ({
			locationId: ta.deliveryLocationId,
			lat: ta.geo.latitude,
			lng: ta.geo.longitude,
		}));

		const maxMatchDistanceMeters = 150;

		hasRoute.value.waypoints.forEach((wp, index) => {
			const wpLat = wp[1];
			const wpLng = wp[0];

			let best = null;

			for (let i = 0; i < locIndex.length; i++) {
				const loc = locIndex[i];
				const d = approxDistanceMeters(wpLat, wpLng, loc.lat, loc.lng);

				if (!best || d < best.d) {
					best = { d, locationId: loc.locationId };
				}
			}

			if (best && best.d <= maxMatchDistanceMeters) {
				pushSequence(best.locationId, index + 1);
			}
		});
	} else {
		appointmentsToRoute.forEach((ta, index) => {
			pushSequence(ta.deliveryLocationId, index + 1);
		});
	}

	const finalLocations = appointmentsToRoute.map((ta) => {
		const sequence = getSequenceLabel(ta.deliveryLocationId);
		return {
			id: ta.deliveryLocationId,
			label: ta.deliveryLocation.label,
			deliveryLocationTypeId: ta.deliveryLocation.deliveryLocationTypeId,
			_geo: {
				latitude: ta.geo.latitude,
				longitude: ta.geo.longitude,
			},
			accountId: ta.deliveryLocation.accountId,
			icon: ta.icon || 'storefront',
			color: ta.color,
			appointmentData: ta,
			sequence: sequence,
		};
	});

	// Additionnal locations from tool AllDeliveryLocations
	if (needAdditionalLocations) {
		additionnalLocations.forEach((loc) => {
			const exists = finalLocations.find((ml) => ml.id === loc.id);
			if (!exists) {
				const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === loc.id);
				if (geo && geo.latitude && geo.longitude) {
					loc.lat = geo.latitude;
					loc.lng = geo.longitude;
				}

				finalLocations.push(loc);
			}
		});
	}

	mapLocations.value = finalLocations;

	if (!skipFitAll) {
		nextTick(() => {
			if (mapRef.value) {
				mapRef.value.fitAll();
			}
		});
	}

	nextTick(() => {
		if (mapRef.value) {
			setTimeout(() => {
				mapRef.value?.applyFilters();
			}, 300);
		}
	});
}

function getGeoByDeliveryLocationId(id) {
	const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === id);
	if (!geo) return null;
	return {
		lat: geo.latitude,
		lng: geo.longitude,
	};
}

function buildWaypoints(from, to) {
	if (!from || !to) return null;
	return [
		[from.lng, from.lat],
		[to.lng, to.lat],
	];
}

async function buildRoutesWaypoints() {
	const waypoints = [];
	const appointments = todayAppointments.value
		.filter((ta) => ta.deliveryLocation && ta.status != enumAppointment.status.CANCELLED && ta.status != enumAppointment.status.VALIDATED && ta.deliveryLocationId !== user.deliveryLocationHomeId)
		.map((ta) => {
			const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === ta.deliveryLocationId);

			return {
				...ta,
				geo: geo,
				hasValidGeo: !!(geo && geo.latitude && geo.longitude),
			};
		})
		.filter((ta) => ta.hasValidGeo);
	const userHome = iframeDataBridge.getEcosVM().deliveryLocations.find((deliveryLocation) => deliveryLocation.id === user.deliveryLocationHomeId);
	userCurrentPosition.value = mapRef.value ? mapRef.value.getUserPosition() : null;
	let startPoint = null;
	let endPoint = null;

	if (userCurrentPosition.value && userCurrentPosition.value.lng && userCurrentPosition.value.lat) {
		startPoint = [userCurrentPosition.value.lng, userCurrentPosition.value.lat];
	} else if (userHome && userHome.lng && userHome.lat) {
		startPoint = [userHome.lng, userHome.lat];
	}

	appointments.forEach((appt) => {
		if (appt.geo && appt.geo.latitude && appt.geo.longitude) {
			waypoints.push([appt.geo.longitude, appt.geo.latitude]);
		}
	});

	if (appointments.length > 0) {
		const lastAppointment = appointments[appointments.length - 1];

		if (lastAppointment.subType === enumAppointment.getSubTypeId('NIGHT')) {
			endPoint = [lastAppointment.geo.longitude, lastAppointment.geo.latitude];
		}
	}

	if (!endPoint && userHome && userHome.lng && userHome.lat) {
		endPoint = [userHome.lng, userHome.lat];
	}

	if (startPoint) {
		waypoints.unshift(startPoint);
	}

	if (endPoint && (waypoints.length === 0 || waypoints[waypoints.length - 1][0] !== endPoint[0] || waypoints[waypoints.length - 1][1] !== endPoint[1])) {
		waypoints.push(endPoint);
	}

	return waypoints;
}

async function renderMarkerDetail(markerData, iframeDocument, detailMarkerData) {
	resetDetailMarkerData(detailMarkerData, markerData);

	let appointmentData = typeof markerData.appointmentData == 'string' ? JSON.parse(markerData.appointmentData) : markerData.appointmentData || null;
	let deliveryLocation = null;
	const isAdditionalMarker = !appointmentData;

	// Additional marker (deliveryLocation only)
	if (isAdditionalMarker) {
		deliveryLocation = markerData;
		// get appointment data for this delivery location if exists
		await appointmentService
			.getAppointments({ deliveryLocationId: deliveryLocation.id })
			.then((appointments) => {
				if (appointments && appointments.length > 0) {
					appointmentData = appointments[0];
				}
			})
			.catch(() => {
				appointmentData = null;
			});
	}
	// Classic appointment marker
	else {
		deliveryLocation = appointmentData.deliveryLocation || null;
	}

	const ecosVM = iframeDataBridge.getEcosVM();
	const deliveryLocationId = deliveryLocation?.id || appointmentData?.deliveryLocationId || null;
	detailMarkerData.value.title = markerData.label || '';
	detailMarkerData.value.id = markerData.id || null;
	detailMarkerData.value.phoneNumber = deliveryLocation ? deliveryLocation.phoneNumber || null : null;
	detailMarkerData.value.lat = markerData.lat || markerData.latitude || appointmentData.lat || deliveryLocation?.lat || null;
	detailMarkerData.value.lng = markerData.lng || markerData.longitude || appointmentData.lng || deliveryLocation?.lng || null;

	const addressParts = [];
	if (deliveryLocation) {
		if (deliveryLocation.addressLine) addressParts.push(deliveryLocation.addressLine);
		if (deliveryLocation.addressLine2) addressParts.push(deliveryLocation.addressLine2);
		if (deliveryLocation.city) addressParts.push(deliveryLocation.city);
		if (deliveryLocation.postcode) addressParts.push(deliveryLocation.postcode);
		if (deliveryLocation.country) addressParts.push(deliveryLocation.country);
	}

	detailMarkerData.value.address = addressParts.join(', ');
	detailMarkerData.value.lastVisitComment = '';

	if (deliveryLocationId) {
		try {
			const lastVisit = appointmentData ? await getLastVisitInfo(deliveryLocationId, appointmentData.startDate || null) : null;
			const lastComment = lastVisit && lastVisit.comment && lastVisit.comment.trim() ? lastVisit.comment : '';
			detailMarkerData.value.lastVisitComment = lastComment;
		} catch (e) {
			detailMarkerData.value.lastVisitComment = '';
		}
	}

	if (appointmentData && appointmentData.sps !== undefined && appointmentData.sps !== null) {
		detailMarkerData.value.sps = appointmentData.sps;
	} else {
		detailMarkerData.value.sps = null;
	}

	detailMarkerData.value.accountLogo = null;
	detailMarkerData.value.accountName = '';

	if (markerData.accountId) {
		const account = ecosVM.accounts && typeof ecosVM.accounts === 'function' ? ecosVM.accounts().find((a) => a.id === markerData.accountId) : (ecosVM.accounts || []).find((a) => a.id === markerData.accountId);

		if (account) {
			detailMarkerData.value.accountName = account.Name || account.name || '';

			const imageUId = account.imageUId || account.ImageUId || null;
			if (imageUId && imageUId !== '00000000-0000-0000-0000-000000000000') {
				try {
					if (iframeDataBridge.isOnline()) {
						const { getAccountImageUrl } = await import('@/api/core/file');
						const imageUrl = await getAccountImageUrl(imageUId);
						detailMarkerData.value.accountLogo = imageUrl || null;
					}
				} catch (e) {
					detailMarkerData.value.accountLogo = null;
				}
			}
		}
	}

	const fields = {};

	const deliveryLocationTypes = ecosVM.deliveryLocationTypes || [];
	const deliveryLocationTypeId = deliveryLocation ? deliveryLocation.deliveryLocationTypeId : markerData.deliveryLocationTypeId || null;
	const deliveryLocationType = deliveryLocationTypes.find((type) => type.id === deliveryLocationTypeId);
	if (deliveryLocationType) {
		fields[t('Type')] = deliveryLocationType.name || deliveryLocationType.reference || '';
	}

	if (appointmentData && appointmentData.startDate) {
		const startDate = new Date(appointmentData.startDate);
		fields[t('Date')] = startDate.toLocaleDateString();

		if (appointmentData.endDate) {
			const endDate = new Date(appointmentData.endDate);
			const startTime = startDate.toLocaleTimeString().substring(0, 5);
			const endTime = endDate.toLocaleTimeString().substring(0, 5);
			fields[t('Hours')] = `${startTime} - ${endTime}`;
		}
	}

	if (appointmentData && appointmentData._statements && appointmentData._statements.length > 0 && appointmentData.startDate) {
		fields[t('Date_Last_Statement')] = new Date(appointmentData.startDate).toLocaleDateString();
	}

	if (deliveryLocationId) {
		try {
			const frequency = await getVisitFrequency(deliveryLocationId);
			if (frequency) {
				fields[t('CurrentVisitRate')] = frequency;
			}

			const kpis = appointmentData ? await getDeliveryLocationKPIs(deliveryLocationId, appointmentData) : [];
			detailMarkerData.value.kpis = Array.isArray(kpis) ? kpis : [];
		} catch (e) {
			detailMarkerData.value.kpis = [];
		}
	} else {
		detailMarkerData.value.kpis = [];
	}

	if (detailMarkerData.value.address) {
		fields[t('Address')] = detailMarkerData.value.address;
	}

	detailMarkerData.value.fields = fields;

	highlightAppointment(detailMarkerData.value);
}

function highlightAppointment(detailMarkerData) {
	todayAppointments.value.forEach((ta) => {
		ta.isHighlighted = false;
	});

	if (!detailMarkerData) return;

	const clickedMapLocation = todayAppointments.value.find((ta) => ta.deliveryLocationId === detailMarkerData.id);
	if (clickedMapLocation) {
		clickedMapLocation.isHighlighted = true;
	}
}

function getRouteSegmentStart() {
	if (!nextAppointmentAfterCompleted.value) return 0;

	const lastCompletedIndex = todayAppointments.value.findIndex((apt) => apt.status === enumAppointment.status.VALIDATED && apt.deliveryLocationId !== user.deliveryLocationHomeId);

	if (lastCompletedIndex === -1) return 0;

	const validAppointments = todayAppointments.value.filter((apt) => apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED);

	const completedCount = validAppointments.filter((apt) => apt.status === enumAppointment.status.VALIDATED).length;

	return ((completedCount - 0.1) / validAppointments.length) * 100;
}

function getRouteSegmentEnd() {
	if (!nextAppointmentAfterCompleted.value) return 0;

	const validAppointments = todayAppointments.value.filter((apt) => apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED);

	const completedCount = validAppointments.filter((apt) => apt.status === enumAppointment.status.VALIDATED).length;

	return ((completedCount + 0.5) / validAppointments.length) * 100;
}

// #endregion

// #region ORDERS

function getOrders() {
	const assortOrderOption = iframeDataBridge.getEcosVM().options.find((opt) => opt.name === 'ProductAssortOrder');

	if (!assortOrderOption || (assortOrderOption && assortOrderOption.value !== 'true')) {
		hasAssortOrder.value = false;
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const success = function (result) {
			let orders = result.filter((order) => {
				return order.type === enumProduct.orderType.ASSORT_REGULAR && new Date(order.createdDate).toDateString() === new Date().toDateString();
			});

			const groups = orders.map((order) => ({
				id: order.id,
				totalDiscountValue: Math.round(order.lines.reduce((sum, line) => sum + (line.valueDiscounted || 0), 0) * 100) / 100,
			}));

			const totalDiscountValue = groups.reduce((sum, group) => sum + group.totalDiscountValue, 0);
			salesAmount.value = `${totalDiscountValue} ${iframeDataBridge.getExternis().sales.salesHelper.getCurrency()}`;
			resolve();
		};

		if (iframeDataBridge.isOnline()) {
			const url = iframeDataBridge.getService().sales.orders.getUrl();
			const method = iframeDataBridge.getService().sales.orders.type || 'get';
			const data = { orderTypes: [enumProduct.orderType.ASSORT_REGULAR] };
			const headers = { Token: user.tokenSales };

			axios({
				method: method.toLowerCase(),
				url,
				headers,
				...(method.toUpperCase() === 'GET' ? { params: data } : { data }),
			})
				.then((response) => success(response.data))
				.catch((error) => {
					console.log(error.response || error);
					resolve();
				});
		} else {
			iframeDataBridge.getStorage().get('ORDERSFULL', success);
		}
	});
}

// #endregion

// #region MENU

function handleMenuClick(appointment, action) {
	if (action === 'map') showOnMap(appointment);
	if (action === 'cancel') cancelAppointment(appointment);
	if (action === 'navigate') {
		selectedAppointmentForNavigation.value = appointment;
		const links = getNavigationLinks();
		if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
			window.open(links.googleMaps, '_blank');
		} else {
			window.open(links.googleMaps, '_blank');
		}
		selectedAppointmentForNavigation.value = null;
	}
	openMenuId.value = null;
}

// #endregion

// #region Filters & Toolbox

function onFiltersChanged(newFilteredLocations) {
	mapLocations.value = newFilteredLocations || [];
}

function onToolboxAction(actionData) {
	switch (actionData.toolId) {
		case 'allDeliveryLocations':
			updateMapLocations();
			break;

		default:
			break;
	}
}

// #endregion

function handleActionFocus() {
	if (!isFullscreen.value) {
		toggleFullscreen();
	}
}

function toggleFullscreen() {
	isFullscreen.value = !isFullscreen.value;
	showMap.value = isFullscreen.value;

	if (!isFullscreen.value && mapRef.value?.hideDetailMarker) {
		mapRef.value.hideDetailMarker();
	}

	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myday',
		fullScreened: isFullscreen.value,
		callback: async () => {
			resizeMap();
			resizeProgressBar();
			if (isFullscreen.value) {
				await nextTick();
				if (mapRef.value && mapRef.value.fitAll) {
					mapRef.value.fitAll();
				}
			}
		},
	});
}

function exitFullscreen() {
	isFullscreen.value = false;
	showMap.value = false;
	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myday',
		fullScreened: isFullscreen.value,
	});
}

function resizeMap() {
	nextTick(() => {
		if (mapRef.value && mapRef.value.resize) {
			mapRef.value.resize();
			mapRef.value.resizeDetailMarkerContent();
		}
	});
}

function resizeProgressBar() {
	setTimeout(() => {
		if (!timelineProgressBar.value || !isTouchMode.value) return;
		const timeLineItems = timelineContainerRef.value ? timelineContainerRef.value.querySelectorAll('.v-timeline-divider, .route-info-item').length : 0;
		const appointmentRougeInfosHeight = root.value ? root.value.querySelectorAll('.appointment-rouge-info').offsetHeight : 0;
		timelineProgressBar.value.style.height = isTouchMode.value ? timeLineItems * (46 + 24) - appointmentRougeInfosHeight + 'px' : '100%';
	}, 300);
}

defineExpose({
	toggleFullscreen,
	isFullscreen,
});
</script>

<style lang="scss" scoped>
.no-appointments-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	color: #757575;
	height: 100%;

	.no-appointments-icon {
		font-size: 48px;
		width: 48px;
		height: 48px;
		margin-bottom: 16px;
		opacity: 0.6;
	}

	p {
		margin: 0;
		font-size: 16px;
		font-weight: 500;
	}
}
</style>
